import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, Package, Wrench, Thermometer, Scale, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products } from '../mock';

export const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find((p) => p.id === id);
  const [selectedImage, setSelectedImage] = useState(0);
  const { t } = useTranslation();

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">{t('productDetail.backToProducts')}</h1>
        <Button asChild>
          <Link to="/products">{t('productDetail.backToProducts')}</Link>
        </Button>
      </div>
    );
  }

  // Get translated product data
  const productData = t(`products.items.${product.id}`, { returnObjects: true });
  const translatedProduct = {
    ...product,
    name: productData?.name || product.id,
    shortDesc: productData?.shortDesc || '',
    material: productData?.material || '',
    wheelType: productData?.wheelType || '',
    loadCapacity: productData?.loadCapacity || '',
    tempResistance: productData?.tempResistance || '',
    compliance: productData?.compliance || ''
  };

  // Translate colors
  const getColorTranslation = (color) => {
    return t(`products.colors.${color}`, { defaultValue: color });
  };

  // Translate customization options
  const getCustomizationTranslation = (customization) => {
    return t(`products.customizations.${customization}`, { defaultValue: customization });
  };

  // Translate type
  const getTypeTranslation = (type) => {
    const typeMap = {
      'residential': t('products.residential'),
      'municipal': t('products.municipal'),
      'industrial': t('products.industrial')
    };
    return typeMap[type] || type;
  };

  const imageGallery = [
    product.image,
    product.image2,
    product.image3
  ];

  return (
    <div className="flex flex-col">
      {/* Breadcrumb */}
      <div className="bg-neutral-50 border-b border-neutral-200 py-3 sm:py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" size="sm" className="mb-0 text-xs sm:text-sm">
            <Link to="/products">
              <ArrowLeft size={14} className="mr-1 sm:mr-2" />
              {t('productDetail.backToProducts')}
            </Link>
          </Button>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-6 sm:py-8 lg:py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Image Gallery */}
            <div>
              <div className="aspect-square bg-neutral-100 rounded-lg overflow-hidden mb-3 sm:mb-4">
                <img 
                  src={imageGallery[selectedImage]} 
                  alt={translatedProduct.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {imageGallery.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square bg-neutral-100 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-emerald-600' : 'border-transparent hover:border-neutral-300'
                    }`}
                  >
                    <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4 sm:mb-6">
                <Badge className="mb-2 sm:mb-3 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs sm:text-sm">{getTypeTranslation(product.type)}</Badge>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">{translatedProduct.name}</h1>
                <p className="text-base sm:text-lg lg:text-xl text-neutral-600 mb-3 sm:mb-4 lg:mb-6">{translatedProduct.shortDesc}</p>
                <div className="flex items-baseline gap-2 mb-4 sm:mb-6">
                  <span className="text-2xl sm:text-3xl font-bold text-emerald-600">{product.capacity}</span>
                  <span className="text-neutral-500 text-sm sm:text-base">{t('productDetail.capacity')}</span>
                </div>
              </div>

              {/* Technical Specs */}
              <Card className="mb-4 sm:mb-6">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="flex items-center text-base sm:text-lg">
                    <Settings className="mr-2" size={18} />
                    {t('productDetail.specifications')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-start">
                      <Package className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-neutral-700">{t('products.material')}</div>
                        <div className="text-xs sm:text-sm text-neutral-600 truncate">{translatedProduct.material}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Wrench className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-neutral-700">{t('products.wheelType')}</div>
                        <div className="text-xs sm:text-sm text-neutral-600">{translatedProduct.wheelType}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Scale className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-neutral-700">{t('products.loadCapacity')}</div>
                        <div className="text-xs sm:text-sm text-neutral-600">{translatedProduct.loadCapacity}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Thermometer className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-neutral-700">{t('products.tempResistance')}</div>
                        <div className="text-xs sm:text-sm text-neutral-600">{translatedProduct.tempResistance}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-medium text-neutral-700">{t('products.compliance')}</div>
                        <div className="text-xs sm:text-sm text-neutral-600">{translatedProduct.compliance}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-4 sm:mb-6">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">{t('productDetail.customization')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Available Colors */}
                  {product.colors && product.colors.length > 0 && (
                    <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-neutral-200">
                      <h4 className="font-semibold text-xs sm:text-sm mb-2 sm:mb-3 text-neutral-800">{t('products.availableColors')}:</h4>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {product.colors.map((color, idx) => (
                          <span 
                            key={idx} 
                            className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 font-medium text-xs sm:text-sm"
                          >
                            {getColorTranslation(color)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <h4 className="font-semibold text-xs sm:text-sm mb-2 sm:mb-3 text-neutral-800">{t('productDetail.customization')}:</h4>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {product.customization.map((option, index) => (
                      <li key={index} className="flex items-center text-xs sm:text-sm">
                        <CheckCircle size={14} className="text-emerald-600 mr-2 flex-shrink-0" />
                        <span>{getCustomizationTranslation(option)}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* CTA */}
              <div className="space-y-2 sm:space-y-3">
                <Button asChild size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-base sm:text-lg">
                  <Link to="/contact">{t('productDetail.requestQuote')}</Link>
                </Button>
                <p className="text-xs sm:text-sm text-neutral-600 text-center">
                  {t('productDetail.bulkPricing')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 sm:py-10 lg:py-12 bg-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 lg:mb-8 text-center">{t('productDetail.features')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <CheckCircle className="text-emerald-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('productDetail.durable')}</h3>
                    <p className="text-xs sm:text-sm text-neutral-600">{t('productDetail.durableDesc')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <CheckCircle className="text-emerald-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('productDetail.maintenance')}</h3>
                    <p className="text-xs sm:text-sm text-neutral-600">{t('productDetail.maintenanceDesc')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="sm:col-span-2 md:col-span-1">
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <CheckCircle className="text-emerald-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('productDetail.uvStabilized')}</h3>
                    <p className="text-xs sm:text-sm text-neutral-600">{t('productDetail.uvDesc')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
